import { BusinessError, SystemUserAssetType } from '@marketing-milk/interfaces'
import { Button } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import Redux from 'app/interface/redux'
import Basic from 'app/redux/actions/pages/business/basic'
import Connect from 'app/redux/connect'
import { businessManagerService } from 'app/service/business-manager'
import {
  FacebookAssetNames,
  FBAssignAssetResult,
  SystemUserIDs,
} from 'app/service/business-manager/BusinessManager.types'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { FBObjectTypeString } from './facebook-object-selector'
import FbAssignableField from './FbAssignableField'
import Popover from 'components/reusable/popover'

// Which FBObjectTypeString(s) each live BusinessError flag maps to, so the panel's per-field health
// chips reflect the existing async check-fb-access results without a new health-check mechanism.
// lead_access_error is deliberately excluded here — it's surfaced as a warning (see
// pageLeadWarningMessage below), not an error, since the Page connection itself still works fine
// without lead access; only lead syncing is affected.
const ERROR_TO_FIELDS: Partial<Record<BusinessError, FBObjectTypeString[]>> = {
  [BusinessError.ad_account_access_error]: ['ad_account'],
  [BusinessError.page_access_error]: ['page'],
  [BusinessError.offline_upload_error]: ['pixel', 'offline'],
}

// lead_access_error is non-fatal — the Page connection itself is fine, only lead syncing is affected —
// so it's shown as a warning on the Page field rather than folded into fieldHasError's red "Needs
// attention" state alongside a real page_access_error.
const pageLeadWarningMessage =
  'This page did not grant Lead Access. Leads will not sync until this is resolved.'

// Maps the backend's SystemUserAssetType (used by fb_business_info) to the selector's
// FBObjectTypeString — the two evolved independently, same as FbAssignableField's FIELD_MAP.
const ASSET_TYPE_TO_FIELD: Record<SystemUserAssetType, FBObjectTypeString> = {
  adaccount: 'ad_account',
  page: 'page',
  dataset: 'pixel',
  offline: 'offline',
  instagram: 'ig',
}

export interface MMFacebookVerificationFields {
  fb_account_id: string
  fb_page_id: string
  fb_dataset_id: string
  fb_offline_id?: string
  instagram_id?: string
}

interface FacebookProps extends Redux {
  switchMode: () => void
  new: boolean
}

const Facebook = (props: FacebookProps) => {
  const { main } = props.pages.business
  const fbConnected = props.core.session.user!.fbConnected ?? false

  const [accountID, setAccountID] = useState<string | undefined>(main.fb_account_id)
  const [pageID, setPageID] = useState<string | undefined>(main.fb_page_id ?? undefined)
  const [datasetID, setDatasetID] = useState<string | undefined>(main.fb_dataset_id ?? undefined)
  const [offlineID, setOfflineID] = useState<string | undefined>(
    main.offline_event_set_id ?? undefined
  )
  const [instagramID, setInstagramID] = useState<string | undefined>(main.instagram_id ?? undefined)
  const [verifying, setVerifying] = useState<boolean>(false)
  const [verified, setVerified] = useState<boolean>(false)
  const [selectionsValid, setSelectionsValid] = useState<boolean>(false)
  const [refreshingAssets, setRefreshingAssets] = useState<boolean>(false)
  // Bumped after clearFacebookAccessCache resolves, to re-trigger the Ad Account/Page fields' own
  // default (empty-search) fetch against the now-cleared server cache — see FacebookObjectSelector's
  // SEARCHED_TYPES comment.
  const [refreshToken, setRefreshToken] = useState(0)
  // How many of the Ad Account/Page fields' post-refresh fetches are still outstanding, plus the
  // resolver for the promise handleRefreshFacebookAccess is awaiting — a ref since it's read and written
  // from callbacks across renders (handleAssetsRefreshed below) rather than something that itself needs
  // to trigger a re-render.
  const pendingAssetFetches = useRef<{ remaining: number; resolve: () => void } | null>(null)

  const handleAssetsRefreshed = () => {
    if (!pendingAssetFetches.current) return
    pendingAssetFetches.current.remaining -= 1
    if (pendingAssetFetches.current.remaining <= 0) {
      pendingAssetFetches.current.resolve()
      pendingAssetFetches.current = null
    }
  }

  // True once any field has been (re)assigned this session — reminds the user that per-field
  // assign-and-verify already ran against Facebook, but the business record itself (and thus these
  // changes) isn't persisted until they hit the panel's own Save button.
  const [hasPendingChanges, setHasPendingChanges] = useState(false)

  // Set when a currently-assigned Dataset/Offline Dataset/Instagram Account turns out to no longer be
  // connected to the ad account after the Ad Account itself changes — none of the three are otherwise
  // re-verified by an Ad Account change alone, since only the field actually being assigned gets checked.
  const [adAccountDependentWarnings, setAdAccountDependentWarnings] = useState<
    Partial<Record<'pixel' | 'offline' | 'ig', string>>
  >({})

  // Run Full Verification's own non-fatal warnings (e.g. page verified but missing lead access) — kept
  // separate from the persisted `errors` array (which only updates on the next real check-fb-access run)
  // so the Page field's warning shows immediately from this verify, without waiting for a stale
  // lead_access_error flag that locallyConfirmedFields.page would otherwise suppress once set below.
  const [verifyWarning, setVerifyWarning] = useState<string | undefined>()

  // Fields confirmed healthy by a real live grant this session, overriding a stale check-fb-access flag
  // for that field until the next Save-triggered check re-evaluates for real. Unlike the reverted
  // "just assume a successful in-session assign means it's fine" approach, this is only set when we have
  // an actual live confirmation — Run Full Verification (verifyFBConnection) either succeeds (meaning
  // the grant call actually completed, not just "didn't throw") or throws, so success here is real proof.
  const [locallyConfirmedFields, setLocallyConfirmedFields] = useState<
    Partial<Record<FBObjectTypeString, true>>
  >({})

  // BM display info per field (id/name/picture) — seeded from fb_business_info on the initial business
  // load (resolved server-side off each asset's system user), then kept in sync locally as per-field
  // assigns happen this session. Not part of BusinessConfig, so it's never sent back on save.
  const [bmInfoByField, setBmInfoByField] = useState<
    Partial<Record<FBObjectTypeString, { id?: string; name?: string; pictureUri?: string }>>
  >(() => {
    const initial: Partial<
      Record<FBObjectTypeString, { id?: string; name?: string; pictureUri?: string }>
    > = {}
    const info = props.pages.business.fb_business_info
    if (info) {
      for (const [assetType, bmInfo] of Object.entries(info) as [
        SystemUserAssetType,
        (typeof info)[SystemUserAssetType]
      ][]) {
        if (bmInfo) {
          initial[ASSET_TYPE_TO_FIELD[assetType]] = {
            id: bmInfo.id,
            name: bmInfo.name,
            pictureUri: bmInfo.pictureUri,
          }
        }
      }
    }
    return initial
  })

  const errors: BusinessError[] = props.pages.business.errors ?? []
  const fieldHasError = (field: FBObjectTypeString) =>
    !locallyConfirmedFields[field] && errors.some(error => ERROR_TO_FIELDS[error]?.includes(field))

  // Dataset/Offline Dataset/Instagram are only re-verified against the ad account when that field
  // itself is (re)assigned — an Ad Account change alone doesn't touch them, so any of the three can
  // silently go stale. Re-check all against the new ad account whenever it changes, purely as read-only
  // checks (no re-grant): dataset connectivity via the dedicated fb-dataset-connection-check endpoint,
  // Instagram by re-listing the ad account's Instagram accounts and checking membership (same call the
  // dropdown itself uses, using the current viewer's own token — this mirrors what the panel already
  // shows as available, not the persisted system-user-token check getBusiness does on load).
  const recheckAdAccountDependentFields = async (newAdAccountId: string) => {
    if (datasetID) {
      try {
        const { connected } = await businessManagerService.checkDatasetConnection(
          datasetID,
          newAdAccountId
        )
        const message = connected
          ? undefined
          : 'This Dataset is no longer connected to the newly selected Ad Account. Check the connection in Events Manager, or update which Dataset is selected.'
        if (message) toast.warn(message)
        setAdAccountDependentWarnings(prev => ({ ...prev, pixel: message }))
      } catch (e) {
        console.error(e)
      }
    }

    if (offlineID) {
      try {
        const { connected } = await businessManagerService.checkDatasetConnection(
          offlineID,
          newAdAccountId
        )
        const message = connected
          ? undefined
          : 'This Offline Dataset is no longer connected to the newly selected Ad Account. Check the connection in Events Manager, or update which Dataset is selected.'
        if (message) toast.warn(message)
        setAdAccountDependentWarnings(prev => ({ ...prev, offline: message }))
      } catch (e) {
        console.error(e)
      }
    }

    if (instagramID) {
      try {
        const igAccounts = await businessManagerService.getFBIGAccounts(newAdAccountId)
        const message = igAccounts.some(ig => ig.id === instagramID)
          ? undefined
          : 'This Instagram account is no longer connected to the newly selected Ad Account.'
        if (message) toast.warn(message)
        setAdAccountDependentWarnings(prev => ({ ...prev, ig: message }))
      } catch (e) {
        console.error(e)
      }
    }
  }

  const handleAssigned = (
    field: FBObjectTypeString,
    result: FBAssignAssetResult,
    assetId: string,
    assetName: string
  ) => {
    setBmInfoByField(prev => ({
      ...prev,
      [field]: {
        id: result.fbBusinessId,
        name: result.fbBusinessName,
        pictureUri: result.fbBusinessPictureUri,
      },
    }))
    setHasPendingChanges(true)

    // A successful per-field assign (Change, Reconnect, or the first-time creator-flow assign) is real
    // proof of current live access — assignSingleFacebookAsset throws on grant failure, so getting here
    // means it didn't. Supersede any stale check-fb-access flag for this field the same way a successful
    // Run Full Verification already does, until the next Save-triggered check re-evaluates for real.
    setLocallyConfirmedFields(prev => ({ ...prev, [field]: true }))

    switch (field) {
      case 'ad_account':
        setAccountID(assetId)
        Basic.update('fb_account_id', assetId)
        Basic.update('fb_account_name', assetName)
        Basic.update('adaccount_system_user_id', result.systemUserId)
        recheckAdAccountDependentFields(assetId)
        break
      case 'page':
        setPageID(assetId)
        Basic.update('fb_page_id', assetId)
        Basic.update('fb_page_name', assetName)
        Basic.update('page_system_user_id', result.systemUserId)
        // A fresh assign supersedes whatever Run Full Verification last found for the page (which could
        // otherwise keep showing a stale lead-access warning after this reassignment already resolved
        // or changed it) — this field's own result.warning (surfaced via localWarning inside
        // FbAssignableField) reflects the current state instead.
        setVerifyWarning(undefined)
        break
      case 'pixel':
        setDatasetID(assetId)
        Basic.update('fb_dataset_id', assetId)
        Basic.update('fb_dataset_name', assetName)
        // DRI-214: a dataset connected to an ad account doesn't go through the system-user grant model
        // at all anymore — result.systemUserId is always absent here now, same as Instagram below, so
        // this stays null rather than passing through undefined (which the backend's Zod schema rejects
        // as a missing required field rather than an explicit null).
        Basic.update('dataset_system_user_id', null)
        // This field's own assign already ran its own connectivity check (reflected in result.warning
        // via the field's normal health chip) — clear any stale warning from an earlier ad-account-only
        // recheck so the two don't show conflicting messages.
        setAdAccountDependentWarnings(prev => ({ ...prev, pixel: undefined }))
        break
      case 'offline':
        setOfflineID(assetId)
        Basic.update('offline_event_set_id', assetId)
        Basic.update('fb_offline_name', assetName)
        // DRI-214: same as pixel above — offline datasets don't get a system-user grant either.
        Basic.update('offline_system_user_id', null)
        setAdAccountDependentWarnings(prev => ({ ...prev, offline: undefined }))
        // Offline Dataset is optional and always shown (no separate enable toggle) — presence of a
        // value here is what use_offline_id (sent to the backend) actually tracks.
        Basic.update('use_offline_id', true)
        break
      case 'ig':
        // Instagram has no system-user-based permission model in this codebase (verifyFacebook never
        // granted one either) — result.systemUserId is always absent here, so this stays null.
        setInstagramID(assetId)
        Basic.update('instagram_id', assetId)
        Basic.update('instagram_name', assetName)
        Basic.update('instagram_system_user_id', null)
        setAdAccountDependentWarnings(prev => ({ ...prev, ig: undefined }))
        break
    }
  }

  // Page, Offline Dataset, and Instagram are the only fields that can be unassigned outright rather than
  // only swapped — Ad Account/Dataset are required for the business to be considered fully configured
  // (see validateBusiness's FB-Builder branch). Clearing is purely local: there's no FB-side grant to
  // revoke, and the change only takes effect once the panel's own Save runs, same as every other field.
  const handleCleared = (field: 'page' | 'offline' | 'ig') => {
    setHasPendingChanges(true)
    setBmInfoByField(prev => ({ ...prev, [field]: undefined }))

    if (field === 'page') {
      setPageID(undefined)
      Basic.update('fb_page_id', null)
      Basic.update('fb_page_name', null)
      Basic.update('page_system_user_id', null)
    } else if (field === 'offline') {
      setOfflineID(undefined)
      Basic.update('offline_event_set_id', null)
      Basic.update('fb_offline_name', null)
      Basic.update('offline_system_user_id', null)
      setAdAccountDependentWarnings(prev => ({ ...prev, offline: undefined }))
      Basic.update('use_offline_id', false)
    } else {
      setInstagramID(undefined)
      Basic.update('instagram_id', null)
      Basic.update('instagram_name', null)
      Basic.update('instagram_system_user_id', null)
      setAdAccountDependentWarnings(prev => ({ ...prev, ig: undefined }))
    }
  }

  const hasAllRequiredSelections = useCallback(() => {
    const hasRequiredSelections = !!accountID && !!pageID && !!datasetID
    return hasRequiredSelections
  }, [accountID, datasetID, pageID])

  useEffect(() => {
    setSelectionsValid(hasAllRequiredSelections())
  }, [accountID, pageID, datasetID, hasAllRequiredSelections])

  const setVerifiedFBConnections = (verified: SystemUserIDs & FacebookAssetNames) => {
    // This is awful, but Basic.update can't update multiple at once and mapping these seems pointless.
    Basic.update('adaccount_system_user_id', verified.adaccountSystemUserID)
    Basic.update('page_system_user_id', verified.pageSystemUserID)
    Basic.update('dataset_system_user_id', verified.datasetSystemUserID)
    Basic.update('offline_system_user_id', verified.offlineSystemUserID)
    Basic.update('instagram_system_user_id', verified.instagramSystemUserID)
    Basic.update('fb_account_name', verified.fbAccountName)
    Basic.update('fb_page_name', verified.fbPageName)
    Basic.update('fb_dataset_name', verified.fbDatasetName)
    Basic.update('fb_offline_name', verified.fbOfflineEventSetName)
    Basic.update('instagram_name', verified.fbInstagramName)
  }

  const verifyFBConnection = async () => {
    if (!accountID || !pageID || !datasetID) {
      toast.warn('Select an Ad Account, Page, and Dataset before verifying access.')
      return
    }
    setVerifying(true)
    try {
      const result = await businessManagerService.verifyFBConnection({
        fb_account_id: accountID,
        fb_page_id: pageID,
        fb_dataset_id: datasetID,
        fb_offline_id: offlineID,
        instagram_id: instagramID,
      })

      setVerifiedFBConnections(result)

      // verifyFacebook grants every provided field in one pass and throws on the first failure — a
      // successful call here proves each of these actually has the required live access right now, so
      // any stale check-fb-access flag for them is superseded until the next real check re-evaluates.
      setLocallyConfirmedFields(prev => ({
        ...prev,
        ad_account: true,
        page: true,
        pixel: true,
        ...(offlineID ? { offline: true as const } : {}),
      }))

      const leadWarning = result.warnings.find(w => w.flag === BusinessError.lead_access_error)
      setVerifyWarning(leadWarning?.message)

      if (result.warnings.length) {
        // Non-fatal — every field still verified, but at least one only did so with a caveat (e.g. a
        // page missing lead access). Distinguish from a hard failure: this is a warning toast, not
        // an error one, and each warning's own field already shows its own message via externalWarning.
        toast.warn(
          `Verified access to your Facebook assets, with ${
            result.warnings.length === 1 ? 'a warning' : 'warnings'
          }: ${result.warnings.map(w => w.message).join(' ')}`
        )
      } else {
        toast.success('Verified access to your Facebook assets.')
      }
      setVerified(true)
    } catch (e) {
      console.error(e)
      toast.error('Access verification failed. Check your access in Facebook.')
      setVerified(false)
    } finally {
      setVerifying(false)
    }
  }

  const handleRefreshFacebookAccess = async () => {
    setRefreshingAssets(true)
    try {
      await businessManagerService.clearFacebookAccessCache()
      // Bumping refreshToken tells the Ad Account/Page fields' own FacebookObjectSelector to refetch
      // their default (empty-search) results against the now-cleared server cache. Awaiting both fields'
      // onRefreshComplete callback means "Refreshing..." covers the real refetches, not just the
      // cache-clear call itself.
      setRefreshToken(prev => prev + 1)
      await new Promise<void>(resolve => {
        pendingAssetFetches.current = { remaining: 2, resolve }
      })
      toast.success('Refreshed available Facebook assets.')
    } catch (e) {
      console.error(e)
      toast.error('Failed to refresh available Facebook assets. Please try again.')
    } finally {
      setRefreshingAssets(false)
    }
  }

  return (
    <>
      <div className="d-flex justify-content-between mb-4">
        <h4 className="px-4">
          <i className="fab fa-facebook"></i>&nbsp;Facebook Builder
        </h4>
        <div className="d-flex align-items-center">
          <Button
            onClick={handleRefreshFacebookAccess}
            className="mr-2"
            variant="contained"
            color="secondary"
            disabled={refreshingAssets}
          >
            {refreshingAssets ? 'Refreshing...' : 'Refresh Available Assets'}
          </Button>
          <Popover>
            This does not re-authenticate your Facebook login — it only refreshes Marketing Milk's
            cached list of Ad Accounts and Pages your current Facebook session can see.
            <br />
            <br />
            Marketing Milk saves what Facebook Ad Accounts and Pages you have access to the first
            time you load this page. If you have updated your Ad Account or Page access in Facebook
            Business Manager, click this button to refresh that list.
            <br />
            <br />
            <strong>
              Warning: This can be slow if you have access to many Ad Accounts and Pages.
            </strong>
          </Popover>
        </div>
      </div>
      <Alert
        style={{
          width: 'fit-content',
          marginLeft: '1.5rem',
          marginTop: '1rem',
          marginBottom: '1.5rem',
          fontSize: '0.95rem',
        }}
        severity="info"
      >
        Marketing Milk manages Facebook access through a system user — an automated identity
        invisible in your Facebook Business Manager's user list — that holds the actual asset
        assignment on our behalf. The Ad Accounts, Pages, and Datasets available for you to assign
        below are limited to what <strong>your own Facebook login</strong> currently is assigned to.
        Only Facebook-signed-in users can reassign these connections.
      </Alert>
      {!fbConnected && (
        <Alert
          style={{
            width: 'fit-content',
            marginLeft: '1.5rem',
            marginTop: '1rem',
            marginBottom: '1.5rem',
            fontSize: '1rem',
          }}
          severity="error"
        >
          You cannot grant access to Facebook assets if you aren't logged in using Facebook.
        </Alert>
      )}
      <div className="row px-3 pb-3">
        <div className="col-md-6 col-sm-12 col-xs-12">
          <FbAssignableField
            type="ad_account"
            title="Facebook Ad Account"
            disabled={!fbConnected}
            accountID={accountID}
            currentAssetId={accountID}
            currentAssetName={main.fb_account_name}
            isNewBusiness={props.new}
            fbBusinessId={bmInfoByField.ad_account?.id}
            fbBusinessName={bmInfoByField.ad_account?.name}
            fbBusinessPictureUri={bmInfoByField.ad_account?.pictureUri}
            hasError={fieldHasError('ad_account')}
            onAssigned={(result, assetId, assetName) =>
              handleAssigned('ad_account', result, assetId, assetName)
            }
            tooltipText="Account to manage paid advertising campaigns and billing on Facebook and Instagram"
            onRefreshAssets={handleRefreshFacebookAccess}
            refreshingAssets={refreshingAssets}
            refreshToken={refreshToken}
            onRefreshComplete={handleAssetsRefreshed}
          />
        </div>
        <div className="col-md-6 col-sm-12 col-xs-12">
          <FbAssignableField
            type="page"
            title="Facebook Page"
            disabled={!fbConnected}
            currentAssetId={pageID}
            currentAssetName={main.fb_page_name}
            isNewBusiness={props.new}
            fbBusinessId={bmInfoByField.page?.id}
            fbBusinessName={bmInfoByField.page?.name}
            fbBusinessPictureUri={bmInfoByField.page?.pictureUri}
            hasError={fieldHasError('page')}
            externalWarning={
              verifyWarning ??
              (!locallyConfirmedFields.page && errors.includes(BusinessError.lead_access_error)
                ? pageLeadWarningMessage
                : undefined)
            }
            onAssigned={(result, assetId, assetName) =>
              handleAssigned('page', result, assetId, assetName)
            }
            clearable
            onCleared={() => handleCleared('page')}
            tooltipText="Public-facing profile for businesses, brands, or organizations on Facebook"
            onRefreshAssets={handleRefreshFacebookAccess}
            refreshingAssets={refreshingAssets}
            refreshToken={refreshToken}
            onRefreshComplete={handleAssetsRefreshed}
          />
        </div>
      </div>
      <div className="row px-3 pb-3">
        <div className="col-md-6 col-sm-12 col-xs-12">
          <FbAssignableField
            type="pixel"
            title="Dataset"
            disabled={!fbConnected}
            accountID={accountID}
            currentAssetId={datasetID}
            currentAssetName={main.fb_dataset_name}
            isNewBusiness={props.new}
            fbBusinessId={bmInfoByField.pixel?.id}
            fbBusinessName={bmInfoByField.pixel?.name}
            fbBusinessPictureUri={bmInfoByField.pixel?.pictureUri}
            externalWarning={adAccountDependentWarnings.pixel}
            hasError={fieldHasError('pixel')}
            onAssigned={(result, assetId, assetName) =>
              handleAssigned('pixel', result, assetId, assetName)
            }
            tooltipText="A collection of data generated from Facebook campaigns, pages, or offline-event uploads."
          />
        </div>
        <div className="col-md-6 col-sm-12 col-xs-12">
          <FbAssignableField
            type="offline"
            title="Offline Dataset"
            optional
            disabled={!fbConnected}
            accountID={accountID}
            currentAssetId={offlineID}
            currentAssetName={main.fb_offline_name}
            isNewBusiness={props.new}
            fbBusinessId={bmInfoByField.offline?.id}
            fbBusinessName={bmInfoByField.offline?.name}
            fbBusinessPictureUri={bmInfoByField.offline?.pictureUri}
            externalWarning={adAccountDependentWarnings.offline}
            hasError={fieldHasError('offline')}
            onAssigned={(result, assetId, assetName) =>
              handleAssigned('offline', result, assetId, assetName)
            }
            clearable
            onCleared={() => handleCleared('offline')}
            tooltipText="Only needed when online and offline data must be managed separately — most businesses can leave this unset and rely on the Dataset above for both."
          />
        </div>
      </div>
      <hr />
      <div className="row px-3 pb-3">
        <div className="col-md-6 col-sm-12 col-xs-12">
          <FbAssignableField
            type="ig"
            title="Instagram Account"
            optional
            disabled={!fbConnected}
            accountID={accountID}
            currentAssetId={instagramID}
            currentAssetName={main.instagram_name}
            isNewBusiness={props.new}
            initialOwnPictureUri={main.instagram_picture_uri}
            fbBusinessId={bmInfoByField.ig?.id}
            fbBusinessName={bmInfoByField.ig?.name}
            fbBusinessPictureUri={bmInfoByField.ig?.pictureUri}
            externalWarning={
              adAccountDependentWarnings.ig ??
              (main.instagram_connected === false
                ? 'This Instagram account is not connected to the currently selected Ad Account.'
                : undefined)
            }
            hasError={false}
            onAssigned={(result, assetId, assetName) =>
              handleAssigned('ig', result, assetId, assetName)
            }
            clearable
            onCleared={() => handleCleared('ig')}
          />
        </div>
      </div>
      <div className="col-sm-12 pt-4 pb-2 d-flex align-items-center justify-content-between flex-wrap">
        {hasPendingChanges ? (
          <div className="d-flex align-items-center" style={{ gap: 8, color: '#95700a' }}>
            <i className="fas fa-exclamation-circle" />
            <span>
              <strong>Changes pending</strong> — assets above are verified with Facebook, but won't
              be saved to this business until you click <strong>Save</strong>.
            </span>
          </div>
        ) : (
          <div />
        )}
      </div>
      <div className="col-sm-12 pb-5 d-flex flex-row-reverse justify-content-between align-items-center flex-grow-0">
        <div className="d-flex flex-column align-items-end">
          <Button
            className="py-2"
            data-testid="create-user-btn"
            variant="contained"
            color="primary"
            startIcon={<i className="fab fa-wrench"></i>}
            onClick={verifyFBConnection}
            disabled={!fbConnected || !selectionsValid || verifying}
          >
            <span>Run{verifying ? 'ning' : ''} Full Verification (Optional)</span>
          </Button>
          <small className="text-muted mt-1">
            Each field above is already verified when you assign it. Use this only as an extra
            end-to-end check before saving.
          </small>
        </div>
        {verified && (
          <Alert
            className="py-0"
            style={{
              width: 'fit-content',
              fontSize: '1.1rem',
            }}
            severity="success"
          >
            Verified access to your Facebook assets. <strong>Save</strong> to keep these settings.
          </Alert>
        )}
      </div>
      <div className="col-sm-12 pb-5">
        <h5>Having Issues?</h5>
        <p>
          This feature is currently in beta. If you are having issues, please report them to the
          team and revert back to using Facebook Authorizers for the time being.
        </p>
        <Button
          data-testid="create-user-btn"
          variant="contained"
          color="secondary"
          onClick={() => props.switchMode()}
        >
          <span>Revert To Facebook Authorizers</span>
        </Button>
      </div>
    </>
  )
}

export default Connect(Facebook)
